import { blue, grey, LocationOpaque } from "@flat-ui";

/** Shared icon size for map control buttons. */
export const WORLD_MAP_CONTROL_ICON_SIZE = "19px";

/** Width of the world map marker icon in pixels. */
export const WORLD_MAP_MARKER_WIDTH_PX = 18;

/** Height of the world map marker icon in pixels. */
export const WORLD_MAP_MARKER_HEIGHT_PX = 24;

/** Hex color baked into the `LocationOpaque` SVG. Used as the replacement anchor for recoloring. */
const BASE_MARKER_COLOR = "#ff4500";
const MAP_MARKER_COLOR = blue[500];
export const MAP_MARKER_HIGHLIGHT_COLOR = blue[700];

/**
 * Returns the marker SVG data URL recolored with the given fill color.
 * Falls back to the original (blue) marker when the requested color matches the baked-in color.
 */
function getMarkerUrl(color) {
  if (color === BASE_MARKER_COLOR) {
    return LocationOpaque;
  }
  return LocationOpaque.replace(encodeURIComponent(BASE_MARKER_COLOR), encodeURIComponent(color));
}

/**
 * Shared picture-marker geometry (size + y-offset) used by both the data layer
 * renderer and the hover/selected highlight overlay.
 */
function getBaseMarkerSymbolProps(color) {
  return {
    url: getMarkerUrl(color),
    width: `${WORLD_MAP_MARKER_WIDTH_PX}px`,
    height: `${WORLD_MAP_MARKER_HEIGHT_PX}px`,
    // Offset the marker vertically so that the point of the marker is at the exact coordinates
    // instead of the center of the marker image.
    yoffset: `${WORLD_MAP_MARKER_HEIGHT_PX / 2}px`
  };
}

/** Builds the picture-marker symbol for the data layer renderer. */
export function getMarkerSymbol() {
  return {
    type: "picture-marker",
    ...getBaseMarkerSymbolProps(MAP_MARKER_COLOR)
  };
}

/**
 * Builds the picture-marker symbol used for the hover/selected highlight overlay.
 *
 * @param scale - uniform size multiplier applied to the base marker (1 = original size).
 */
export function getHighlightMarkerSymbolProps(scale = 1) {
  const width = WORLD_MAP_MARKER_WIDTH_PX * scale;
  const height = WORLD_MAP_MARKER_HEIGHT_PX * scale;
  return {
    url: getMarkerUrl(MAP_MARKER_HIGHLIGHT_COLOR),
    width: `${width}px`,
    height: `${height}px`,
    // Keep the marker tip anchored at the coordinate (yoffset = height / 2).
    yoffset: `${height / 2}px`
  };
}

/**
 * Distance (in screen pixels) the 3D marker is lifted above its anchor on the
 * scene surface. The icon floats this far above rooftops with a callout leader
 * line, so buildings no longer occlude it.
 */
const MARKER_3D_VERTICAL_OFFSET_SCREEN_PX = 40;

/**
 * Lower clamp (meters) for the 3D marker lift. Kept small so the lift stays
 * screen-anchored (~{@link MARKER_3D_VERTICAL_OFFSET_SCREEN_PX} px) as the camera
 * zooms in, keeping the pin and its callout line on screen instead of flying off
 * the top of the viewport. The small non-zero floor keeps the pin a hair above
 * the surface so it doesn't clip into sloped/elevated terrain.
 */
const MARKER_3D_VERTICAL_OFFSET_MIN_WORLD_M = 8;

/**
 * Upper clamp (meters) for the 3D marker lift. When zoomed far out the 40 px
 * screen offset would map to kilometers; capping it here keeps the pin from
 * floating unrealistically high, and the capped height projects to sub-pixel at
 * that distance so the pin collapses onto the surface (tip on the location, no
 * visible callout line).
 */
const MARKER_3D_VERTICAL_OFFSET_MAX_WORLD_M = 200;

/** Thickness (pixels) of the 3D marker callout leader line. */
const MARKER_3D_CALLOUT_SIZE_PX = 1.5;

/** Color of the thin border around the 3D marker callout line, for contrast against buildings. */
const MARKER_3D_CALLOUT_BORDER_COLOR = grey[0];

/**
 * Minimum camera tilt (degrees) at which 3D markers switch from resting on the
 * ground to being lifted above the surface with a callout leader line. Below this
 * the camera is near top-down, where nothing occludes a ground marker, so the pin
 * sits directly on its coordinate with no line; at or above it, buildings begin to
 * occlude ground markers, so the pin is raised to stay visible.
 */
export const MARKER_3D_LIFT_MIN_TILT_DEG = 15;

/**
 * Shared 3D point-symbol geometry used by the 3D data-layer renderer. The marker
 * icon is anchored at its bottom (the pin's point rests on the coordinate). When
 * `isLifted` is true (camera tilted past {@link MARKER_3D_LIFT_MIN_TILT_DEG}) the
 * icon is raised above the surface with a callout leader line so 3D building
 * geometry no longer occludes it; otherwise it rests on the ground with no line.
 */
function getBaseMarkerSymbol3DProps(color, {
  scale = 1,
  isLifted = false
} = {}) {
  const props = {
    type: "point-3d",
    symbolLayers: [{
      type: "icon",
      resource: {
        href: getMarkerUrl(color)
      },
      size: WORLD_MAP_MARKER_HEIGHT_PX * scale,
      // Rest the icon's bottom (the pin's point) on the coordinate.
      anchor: "bottom"
    }]
  };
  if (isLifted) {
    props.verticalOffset = {
      screenLength: MARKER_3D_VERTICAL_OFFSET_SCREEN_PX,
      maxWorldLength: MARKER_3D_VERTICAL_OFFSET_MAX_WORLD_M,
      minWorldLength: MARKER_3D_VERTICAL_OFFSET_MIN_WORLD_M
    };
    props.callout = {
      type: "line",
      size: MARKER_3D_CALLOUT_SIZE_PX,
      color,
      border: {
        color: MARKER_3D_CALLOUT_BORDER_COLOR
      }
    };
  }
  return props;
}

/**
 * Builds the 3D point symbol for the data-layer renderer. SceneView cannot render
 * 2D picture markers as a feature renderer, so the marker is drawn as a `point-3d`
 * icon anchored at its bottom. Pass `isLifted` to raise it above buildings with a
 * callout when the camera is tilted (see {@link MARKER_3D_LIFT_MIN_TILT_DEG}).
 */
export function getMarkerSymbol3D(isLifted = false) {
  return getBaseMarkerSymbol3DProps(MAP_MARKER_COLOR, {
    isLifted
  });
}

/**
 * Builds the 3D point symbol used for the hover/selected highlight overlay in 3D,
 * optionally enlarged. Shares the grounded/lifted geometry of the data marker so
 * the highlight stays aligned with the pin it replaces.
 */
export function getHighlightMarkerSymbol3D(scale = 1, isLifted = false) {
  return getBaseMarkerSymbol3DProps(MAP_MARKER_HIGHLIGHT_COLOR, {
    scale,
    isLifted
  });
}

/** Builds the GeoJSONLayer configuration for the world map data layer. */
export function getWorldMapGeoJSONLayerConfig(is3DEnabled = false) {
  // SceneView does not support cluster feature reduction (confirmed against the
  // FeatureReductionCluster "Known Limitations": not supported in 3D SceneView).
  // In 3D we render every marker individually as a 3D point symbol, lifted above
  // the scene surface with a callout line (and draped onto terrain/buildings via
  // elevationInfo) so the pin floats above building geometry instead of being
  // occluded by it.
  if (is3DEnabled) {
    return {
      outFields: ["*"],
      renderer: {
        type: "simple",
        symbol: getMarkerSymbol3D()
      },
      elevationInfo: {
        mode: "relative-to-scene"
      }
    };
  }
  return {
    outFields: ["*"],
    // Marker
    renderer: {
      type: "simple",
      symbol: getMarkerSymbol()
    },
    // Cluster
    featureReduction: {
      type: "cluster",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      popupEnabled: false,
      clusterRadius: "100px",
      clusterMinSize: "32px",
      clusterMaxSize: "32px",
      labelingInfo: [
      // Label showing number of clustered features
      {
        deconflictionStrategy: "none",
        labelPlacement: "center-center",
        labelExpressionInfo: {
          expression: "$feature.cluster_count"
        },
        symbol: {
          type: "text",
          // Move the label down to compensate for the y-offset applied to the marker symbol,
          // so that the label is centered within the cluster.
          yoffset: "-3px",
          color: grey[0],
          font: {
            size: "12px",
            weight: "bold"
          }
        }
      }]
    }
  };
}

/** Latitude to center the world map view on initialization */
export const WORLD_MAP_VIEW_CENTER_LAT = 50;

/** Longitude to center the world map view on initialization */
export const WORLD_MAP_VIEW_CENTER_LON = 10;

/** Initial zoom level for the world map view on initialization */
export const WORLD_MAP_INITIAL_ZOOM = 0;

/** Smallest zoom level the view is allowed to reach. */
export const WORLD_MAP_MIN_ZOOM = 0;

/** Maximum allowed zoom level. */
export const WORLD_MAP_MAX_ZOOM = 20;

/** Zoom level to focus on a specific position on the world map */
export const WORLD_MAP_FOCUS_ZOOM = 16;

/**
 * Zoom level used when the map fits to a *single* project (on initial load or
 * reset view). Lower than {@link WORLD_MAP_FOCUS_ZOOM} so more of the location's
 * surroundings are visible, since there are no other markers to frame against.
 */
export const WORLD_MAP_SINGLE_ENTITY_ZOOM = 13;

/**
 * Camera tilt (degrees) used when focusing on a position in 3D. 0 looks straight
 * down (top-down); 90 looks at the horizon. A moderate tilt gives an angled,
 * perspective view of the location rather than a flat overhead one.
 * MapView (2D) ignores tilt.
 */
export const WORLD_MAP_FOCUS_TILT = 60;

/**
 * Great-circle distance (kilometers) beyond which a fly-to ("go to") animation
 * is shortened. An animated `goTo` renders every intermediate view along the
 * flight path, streaming tiles for regions the user only flies over, so a very
 * long trip is slow and loads hundreds of throwaway tiles. Trips shorter than
 * this keep the default (natural-length) animation; longer trips are capped to
 * {@link WORLD_MAP_GOTO_ANIMATION_MAX_DURATION_MS}.
 */
export const WORLD_MAP_GOTO_ANIMATION_MAX_DISTANCE_KM = 1500;

/**
 * Maximum duration (milliseconds) of a fly-to ("go to") animation for trips
 * beyond {@link WORLD_MAP_GOTO_ANIMATION_MAX_DISTANCE_KM}. Bounds how long a
 * far journey animates (and therefore how many intermediate tiles it streams)
 * while still conveying the sense of travel, instead of jumping instantly.
 */
export const WORLD_MAP_GOTO_ANIMATION_MAX_DURATION_MS = 1200;

/**
 * Zoom levels applied per unit of mouse-wheel / trackpad delta. The default
 * ArcGIS wheel zoom feels sluggish on laptop trackpads (which emit many small
 * deltas), so in 3D we drive the zoom ourselves with this sensitivity.
 */
export const WORLD_MAP_WHEEL_ZOOM_SENSITIVITY = 0.01;

/**
 * Caps the per-event wheel delta before applying the sensitivity. A single mouse
 * notch reports a large delta (~100), which would otherwise jump several zoom
 * levels at once; clamping keeps each notch to a small, predictable step while
 * leaving the many small trackpad deltas untouched.
 */
export const WORLD_MAP_WHEEL_MAX_DELTA = 40;

/** Degrees of camera heading rotation per pixel of horizontal orbit drag (3D). */
export const WORLD_MAP_ORBIT_HEADING_SENSITIVITY = 0.3;

/** Degrees of camera tilt change per pixel of vertical orbit drag (3D). */
export const WORLD_MAP_ORBIT_TILT_SENSITIVITY = 0.3;

/** Maximum camera tilt (degrees) allowed while orbiting in 3D. */
export const WORLD_MAP_ORBIT_MAX_TILT = 80;

/** Default fields for the world map GeoJSON layer */
export const WORLD_MAP_DEFAULT_FIELDS = [{
  name: "name",
  type: "string"
}, {
  name: "id",
  type: "string"
}];

/** ArcGIS named basemap id used for each map style. */
export const ARCGIS_BASEMAP_ID_BY_MAP_STYLE = {
  colored: "topo-3d",
  satellite: "hybrid"
};

/** Default map style for the world map */
export const WORLD_MAP_DEFAULT_MAP_STYLE = "colored";

/** Default rendering mode for the world map */
export const WORLD_MAP_DEFAULT_MAP_MODE = "2D";

/**
 * Whether the 2D map mode (and its 2D/3D toggle button) is exposed to users.
 *
 * Currently `false`: the product shows only the 3D globe (Apple-Maps-style), so
 * the map always renders the `SceneView` and the 2D/3D toggle button is hidden.
 * The entire 2D `MapView` code path is intentionally kept behind this flag so the
 * mode can be brought back simply by flipping this to `true` — stakeholders are
 * likely to request it again. Typed as `boolean` (not the literal `false`) so the
 * kept 2D branches are not treated as dead code.
 */
export const isWorldMap2DModeEnabled = false;